import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Bot, MessageSquare, TrendingUp, DollarSign, Users, Shield, BarChart } from 'lucide-react';

const AIPainPointSolutions = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const solutions = [
    {
      category: 'Customer Service',
      icon: MessageSquare,
      color: 'bg-blue-100 text-blue-600',
      painPoints: [
        {
          title: 'High Volume of Repetitive Questions',
          impact: 'Critical',
          aiSolution: {
            approach: 'Intelligent FAQ & Knowledge Base Automation',
            implementation: [
              'Deploy AI chatbot trained on product catalog, policies, and common questions',
              'Natural Language Understanding (NLU) to interpret customer intent',
              'Auto-responses for 15-20 most common question categories',
              'Seamless handoff to human agents for complex queries'
            ],
            channels: ['Website Chat', 'WhatsApp', 'Facebook Messenger', 'Instagram DM'],
            results: '70-80% of routine queries automated, 24/7 availability',
            metrics: 'Response time: <5 seconds, Resolution rate: 75-85%, Cost savings: 50-60%',
            roi: '$3,000-5,000/month savings per 1,000 conversations'
          }
        },
        {
          title: '24/7 Support Expectations Without Night Staff Costs',
          impact: 'High',
          aiSolution: {
            approach: 'Round-the-Clock AI Support Agents',
            implementation: [
              'Voice bot for phone support during off-hours',
              'Multi-language chat support for global customers',
              'Timezone-aware response system',
              'Escalation protocols for urgent issues to on-call staff'
            ],
            channels: ['Voice Bot', 'Website Chat', 'SMS/WhatsApp'],
            results: '100% availability without night shift hiring',
            metrics: 'After-hours queries handled: 95%+, Customer satisfaction: 4.2/5 avg',
            roi: 'Save $4,000-8,000/month vs hiring night shift team'
          }
        },
        {
          title: 'High Cart Abandonment (70%+)',
          impact: 'Critical',
          aiSolution: {
            approach: 'Proactive Cart Recovery System',
            implementation: [
              'Trigger chatbot when exit intent detected',
              'Ask qualifying questions: "Need help deciding?"',
              'Address concerns in real-time (shipping, pricing, product fit)',
              'Offer incentives (discount codes) when appropriate',
              'Follow-up via WhatsApp/SMS with personalized messages',
              'A/B test different recovery messages'
            ],
            channels: ['Website Chat', 'WhatsApp', 'SMS', 'Email'],
            results: 'Recover 15-25% of abandoned carts',
            metrics: 'Engagement rate: 35-45%, Conversion uplift: 15-25%, Revenue recovery: $10-30K/month',
            roi: '$15,000-40,000 additional revenue per month for $100K/month store'
          }
        }
      ]
    },
    {
      category: 'Sales & Revenue',
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600',
      painPoints: [
        {
          title: 'Poor Product Discovery & Selection',
          impact: 'Critical',
          aiSolution: {
            approach: 'AI-Powered Product Recommendation Engine',
            implementation: [
              'Conversational product finder: "What are your health goals?"',
              'Quiz-based recommendations (5-7 questions)',
              'Compare products side-by-side based on ingredients',
              'Explain "why" this product fits their needs',
              'Bundle suggestions for complementary products',
              'Integration with product database and inventory'
            ],
            channels: ['Website Chat', 'Voice Bot', 'WhatsApp'],
            results: 'Reduce decision time by 60%, increase AOV by 25-35%',
            metrics: 'Product finder completion: 65%+, Conversion rate: 35-45%, AOV increase: $15-25',
            roi: '$20,000-50,000 additional monthly revenue'
          }
        },
        {
          title: 'Low Customer Lifetime Value (CLV)',
          impact: 'High',
          aiSolution: {
            approach: 'Automated Retention & Subscription Conversion',
            implementation: [
              'Post-purchase check-ins via WhatsApp (Day 7, 15, 30)',
              'Usage guidance and tips for better results',
              'Proactive reorder reminders based on product depletion',
              'Subscription incentive offers at optimal time',
              'Personalized cross-sell based on purchase history',
              'Win-back campaigns for lapsed customers'
            ],
            channels: ['WhatsApp', 'SMS', 'Email with Chat Widget'],
            results: 'Increase repeat purchase rate by 30-40%, subscription conversion 15-20%',
            metrics: 'Engagement rate: 45-55%, Repeat purchase uplift: 30-40%, Subscription rate: +15%',
            roi: 'CLV increase of $50-100 per customer'
          }
        },
        {
          title: 'Long Sales Cycles & Multiple Touchpoints',
          impact: 'Medium',
          aiSolution: {
            approach: 'Nurture Campaign Automation with AI',
            implementation: [
              'Capture leads through chat (email/phone)',
              'Segment by interest, concern type, budget',
              'Automated drip campaigns via preferred channel',
              'Educational content delivery (studies, testimonials)',
              'Progressive profiling through conversations',
              'Behavioral triggers for timely follow-ups'
            ],
            channels: ['WhatsApp', 'SMS', 'Email', 'Website Chat'],
            results: 'Shorten sales cycle by 30-40%, increase lead-to-customer conversion',
            metrics: 'Lead capture rate: 25-35%, Nurture engagement: 40-50%, Conversion rate: +20%',
            roi: '$10,000-25,000 additional monthly revenue from nurtured leads'
          }
        }
      ]
    },
    {
      category: 'Operations & Costs',
      icon: DollarSign,
      color: 'bg-purple-100 text-purple-600',
      painPoints: [
        {
          title: 'High Customer Acquisition Cost (CAC)',
          impact: 'Critical',
          aiSolution: {
            approach: 'Maximize Value from Existing Traffic',
            implementation: [
              'Increase conversion rate through better engagement',
              'Reduce bounce rate with proactive chat',
              'Capture more leads from non-buyers',
              'Improve ad landing page experience with chat',
              'A/B test messaging to optimize conversion',
              'Analytics on visitor behavior and objections'
            ],
            channels: ['Website Chat', 'Landing Page Bots'],
            results: 'Improve ROAS by 30-50%, reduce effective CAC by 20-30%',
            metrics: 'Conversion rate: +25-40%, Bounce rate: -15-20%, Lead capture: +30%',
            roi: 'Better CAC efficiency worth $5,000-15,000/month in marketing savings'
          }
        },
        {
          title: 'Customer Support Overhead Costs',
          impact: 'High',
          aiSolution: {
            approach: 'Hybrid AI-Human Support Model',
            implementation: [
              'AI handles Tier 1 support (70-80% of volume)',
              'Human agents focus on complex issues only',
              'AI provides suggested responses to agents',
              'Continuous learning from agent interactions',
              'Reduce agent workload, increase satisfaction',
              'Scale support without proportional hiring'
            ],
            channels: ['All Channels Unified'],
            results: 'Handle 3-5x more conversations with same team size',
            metrics: 'Automation rate: 70-80%, Agent productivity: +150%, Cost per conversation: -60%',
            roi: '$40,000-80,000 annual savings per support agent not hired'
          }
        },
        {
          title: 'Scaling Challenges & Traffic Spikes',
          impact: 'Medium',
          aiSolution: {
            approach: 'Elastic AI Support Capacity',
            implementation: [
              'AI automatically scales to handle any volume',
              'No degradation in response time during spikes',
              'Handle seasonal peaks without temporary hiring',
              'Maintain service quality during promotions',
              'Queue management for human handoffs',
              'Load balancing across channels'
            ],
            channels: ['All Channels'],
            results: 'Handle unlimited concurrent conversations without quality loss',
            metrics: 'Peak capacity: Unlimited, Response time consistency: 100%, Overflow rate: 0%',
            roi: '$10,000-30,000 saved per major sales event vs temporary hiring'
          }
        }
      ]
    },
    {
      category: 'Compliance & Trust',
      icon: Shield,
      color: 'bg-red-100 text-red-600',
      painPoints: [
        {
          title: 'Regulatory Compliance & FDA Restrictions',
          impact: 'Critical',
          aiSolution: {
            approach: 'Compliant AI with Built-in Guardrails',
            implementation: [
              'Train AI on FDA regulations and prohibited claims',
              'Automatic detection and blocking of non-compliant language',
              'Pre-approved response library for sensitive topics',
              'Escalate medical questions to humans immediately',
              'Regular compliance audits of AI responses',
              'Conversation logging for regulatory records'
            ],
            channels: ['All Channels with Compliance Layer'],
            results: 'Zero compliance violations, consistent messaging',
            metrics: 'Compliance rate: 100%, Prohibited claim blocks: 100%, Audit trail: Complete',
            roi: 'Avoid $10,000-100,000+ in potential FDA fines and warnings'
          }
        },
        {
          title: 'Building Trust & Credibility',
          impact: 'High',
          aiSolution: {
            approach: 'Transparent, Educational AI Assistant',
            implementation: [
              'Clearly identify as AI with option for human help',
              'Provide educational content, not just sales pitches',
              'Share studies, certifications, and third-party testing',
              'Acknowledge limitations and redirect appropriately',
              'Collect and display customer testimonials',
              'Professional, empathetic tone throughout'
            ],
            channels: ['Website Chat', 'WhatsApp', 'Voice Bot'],
            results: 'Increase trust scores and customer confidence',
            metrics: 'Trust rating: 4.3/5+, Human escalation satisfaction: 90%+',
            roi: 'Higher conversion rates worth $5,000-15,000/month'
          }
        },
        {
          title: 'Product Information Consistency',
          impact: 'Medium',
          aiSolution: {
            approach: 'Single Source of Truth Knowledge System',
            implementation: [
              'Centralized product database integration',
              'Real-time inventory and pricing updates',
              'Consistent answers across all channels',
              'Version control for product information',
              'Easy updates reflected instantly everywhere',
              'Audit trail of information changes'
            ],
            channels: ['All Channels Synchronized'],
            results: '100% consistency, zero conflicting information',
            metrics: 'Information accuracy: 99%+, Update propagation: Instant, Channel consistency: 100%',
            roi: 'Prevent revenue loss from misinformation, improve customer experience'
          }
        }
      ]
    },
    {
      category: 'Marketing & Engagement',
      icon: Users,
      color: 'bg-orange-100 text-orange-600',
      painPoints: [
        {
          title: 'Low Email/SMS Engagement Rates',
          impact: 'Medium',
          aiSolution: {
            approach: 'Conversational Marketing via Preferred Channels',
            implementation: [
              'Shift from broadcast to two-way conversations',
              'WhatsApp/SMS opt-in during chat interactions',
              'Personalized message timing based on behavior',
              'Interactive content (polls, quizzes) via messaging',
              'Rich media messages (images, videos, carousels)',
              'Response-triggered automations'
            ],
            channels: ['WhatsApp', 'SMS', 'Facebook Messenger'],
            results: 'Engagement rates 5-10x higher than email',
            metrics: 'Open rate: 90%+, Response rate: 40-60%, Click-through: 25-35%',
            roi: '$8,000-20,000 additional monthly revenue from re-engaged customers'
          }
        },
        {
          title: 'Lack of Personalization at Scale',
          impact: 'High',
          aiSolution: {
            approach: 'AI-Driven Hyper-Personalization',
            implementation: [
              'Customer profile building from every interaction',
              'Behavioral tracking and preference learning',
              'Dynamic product recommendations per individual',
              'Personalized content and education',
              'Segment-of-one marketing messages',
              'Adaptive conversation flows based on history'
            ],
            channels: ['All Channels with CRM Integration'],
            results: 'Personalized experience for 100% of customers',
            metrics: 'Recommendation accuracy: 75%+, Relevance score: 4.5/5, Engagement lift: 50%+',
            roi: '$15,000-35,000 additional monthly revenue from better personalization'
          }
        },
        {
          title: 'Post-Purchase Drop-off & Poor Onboarding',
          impact: 'High',
          aiSolution: {
            approach: 'Automated Customer Success Journey',
            implementation: [
              'Welcome series with usage instructions',
              'Educational content drip (benefits, science, tips)',
              'Progress tracking and milestone celebrations',
              'Proactive problem-solving (side effects, questions)',
              'Results expectation management',
              'Subscription conversion at optimal moment'
            ],
            channels: ['WhatsApp', 'SMS', 'Email'],
            results: 'Reduce churn by 25-35%, increase LTV by 40-60%',
            metrics: 'Onboarding completion: 70%+, 90-day retention: +30%, NPS: +15 points',
            roi: '$25,000-60,000 additional annual revenue per 100 customers retained'
          }
        }
      ]
    },
    {
      category: 'Data & Insights',
      icon: BarChart,
      color: 'bg-indigo-100 text-indigo-600',
      painPoints: [
        {
          title: 'Limited Customer Intelligence',
          impact: 'Medium',
          aiSolution: {
            approach: 'Conversational Analytics & Insights Engine',
            implementation: [
              'Automatic tagging of conversation topics and intents',
              'Objection tracking and frequency analysis',
              'Product questions and concerns dashboard',
              'Customer sentiment analysis',
              'Feature request and feedback aggregation',
              'Competitive intelligence from customer mentions'
            ],
            channels: ['Analytics Dashboard from All Channels'],
            results: 'Deep insights into customer needs and pain points',
            metrics: 'Data capture rate: 95%+, Insight actionability: High, Report automation: 100%',
            roi: 'Inform product development, marketing, and strategy worth $20,000-50,000 in better decisions'
          }
        },
        {
          title: 'Inability to Track Customer Journey',
          impact: 'Medium',
          aiSolution: {
            approach: 'Omnichannel Journey Mapping & Attribution',
            implementation: [
              'Unified customer identity across all touchpoints',
              'Track conversations from awareness to purchase',
              'Attribute conversions to specific interactions',
              'Identify high-value conversation patterns',
              'Optimize touchpoint sequence and messaging',
              'Cohort analysis and funnel visualization'
            ],
            channels: ['Unified Analytics Platform'],
            results: 'Complete visibility into customer journey and conversion drivers',
            metrics: 'Journey completeness: 90%+, Attribution accuracy: 85%+, Optimization opportunities: 15-25',
            roi: '$10,000-30,000 monthly revenue improvement from journey optimization'
          }
        }
      ]
    }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', icon: Bot },
    ...solutions.map(s => ({ id: s.category, name: s.category, icon: s.icon }))
  ];

  const filteredSolutions = selectedCategory === 'all' 
    ? solutions 
    : solutions.filter(s => s.category === selectedCategory);

  const toggleSection = (categoryIndex, pointIndex) => {
    const key = `${categoryIndex}-${pointIndex}`;
    setExpandedSection(expandedSection === key ? null : key);
  };

  const impactColors = {
    'Critical': 'bg-red-500',
    'High': 'bg-orange-500',
    'Medium': 'bg-yellow-500'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-24 pb-4 md:pb-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Bot className="w-10 h-10 text-blue-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              AI Solutions for Nutraceutical E-commerce
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Comprehensive plan addressing 17 critical pain points with AI-powered chatbots, voice bots, and messaging automation
          </p>
        </div>

        {/* Category Filter */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Solutions */}
        {filteredSolutions.map((category, catIndex) => {
          const CategoryIcon = category.icon;
          return (
            <div key={catIndex} className="mb-8">
              <div className={`${category.color} rounded-xl p-4 mb-4`}>
                <div className="flex items-center gap-3">
                  <CategoryIcon className="w-6 h-6" />
                  <h2 className="text-2xl font-bold">{category.category}</h2>
                  <span className="ml-auto bg-white px-3 py-1 rounded-full text-sm font-semibold">
                    {category.painPoints.length} Pain Points
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {category.painPoints.map((point, pointIndex) => {
                  const isExpanded = expandedSection === `${catIndex}-${pointIndex}`;
                  return (
                    <div
                      key={pointIndex}
                      className="bg-white rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg"
                    >
                      <button
                        onClick={() => toggleSection(catIndex, pointIndex)}
                        className="w-full p-6 flex items-start gap-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className={`w-2 h-2 mt-2 rounded-full ${impactColors[point.impact]}`} />
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4">
                            <h3 className="text-xl font-bold text-gray-900">{point.title}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                              point.impact === 'Critical' ? 'bg-red-100 text-red-700' :
                              point.impact === 'High' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {point.impact} Impact
                            </span>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                      </button>

                      {isExpanded && (
                        <div className="border-t border-gray-100 p-6 bg-gradient-to-br from-gray-50 to-white">
                          <div className="space-y-6">
                            {/* Approach */}
                            <div>
                              <h4 className="font-bold text-blue-900 mb-2 text-lg">AI Solution Approach</h4>
                              <p className="text-gray-700 font-medium">{point.aiSolution.approach}</p>
                            </div>

                            {/* Implementation */}
                            <div>
                              <h4 className="font-bold text-blue-900 mb-3">Implementation Steps</h4>
                              <ul className="space-y-2">
                                {point.aiSolution.implementation.map((step, i) => (
                                  <li key={i} className="flex items-start gap-3">
                                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold">
                                      {i + 1}
                                    </span>
                                    <span className="text-gray-700 pt-0.5">{step}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Channels */}
                            <div>
                              <h4 className="font-bold text-blue-900 mb-3">Deployment Channels</h4>
                              <div className="flex flex-wrap gap-2">
                                {point.aiSolution.channels.map((channel, i) => (
                                  <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                                    {channel}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Results & Metrics */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h4 className="font-bold text-green-900 mb-2">Expected Results</h4>
                                <p className="text-green-800">{point.aiSolution.results}</p>
                              </div>
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <h4 className="font-bold text-purple-900 mb-2">Key Metrics</h4>
                                <p className="text-purple-800 text-sm">{point.aiSolution.metrics}</p>
                              </div>
                            </div>

                            {/* ROI */}
                            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-5 h-5" />
                                <h4 className="font-bold text-lg">ROI Potential</h4>
                              </div>
                              <p className="text-green-50">{point.aiSolution.roi}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Summary Footer */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Total Impact Summary</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-3xl font-bold mb-2">17</div>
              <div className="text-blue-100">Pain Points Addressed</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">$50K-200K</div>
              <div className="text-blue-100">Potential Monthly Revenue Impact</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">60-80%</div>
              <div className="text-blue-100">Support Cost Reduction</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPainPointSolutions;

